import React, {useEffect, useState, useContext, useRef} from "react";

import IndustryTimeSeries from "../components/IndustryTimeSeries";
import IndustryDataSummary from "../components/IndustryDataSummary";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import IndustryRanking from "../components/IndustryRanking";
import CompanyInformation from "../components/CompanyInformation";
import SaveSettingButton from "../components/SaveSettingButton";
import ManualScatterGraph from "../../graph/ManualScatterGraph";
import SelectScatterColumnContainer from "../components/SelectScatterColumnContainer";
import SimilarCompanyContainer from "../components/SimilarCompanyInformation";
import { UserSettingsContext, getDescriptionBySettingName } from "../../../context/UserSettingsContext";
import Loading from "../../Loading";
import { useDashboardContext } from '../../../context/DashboardContext';
import { fetchIndustryFinancialData, fetchIndustryInfo, fetchIndustryMarketData, fetchIndustryCompanies } from "../../api/api_calls";
import { formatDateTimeString } from "../../utils/commonUtils";
import TwoColumnSwitch from "../../utils/TwoColumnSwitch";


const IndustryInformationTab = ({secCode}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams()
    const [loaded, setLoaded] = useState(false);
    const { state: dashboardState, dispatch: dashboardDispatch } = useDashboardContext();
    const [industryFinancialData, setIndustryFinancialData] = useState(null);
    const [filteredIndustryFinancialData, setFilteredIndustryFinancialData] = useState(null);
    const [individualLatestFinancialData, setIndividualLatestFinancialData] = useState(null);
    const [filteredIndividualLatestFinancialData, setfilteredIndividualLatestFinancialData] = useState(null);
    const [industryMarketData, setIndustryMarketData] = useState(null);
    const [filteredIndustryMarketData, setFilteredIndustryMarketData] = useState(null);
    const [industryInfo, setIndustryInfo] = useState(null);
    const [isTwoColumns, setIsTwoColumns] = useState(true)
    const { userSettings, addUserSetting } = useContext(UserSettingsContext);
    const [selectedHorizontalAccount, setSelectedHorizontalAccount] = useState(null); // 縦軸
    const [selectedVerticalAccount, setSelectedVerticalAccount] = useState(null); // 横軸
    const [graphData, setGraphData] = useState(null);
    const tabContentRef = useRef(null)
    const { selectableAccounts } = useContext(UserSettingsContext);
    const [defaultHorizontalAccount, setDefaultHorizontalAccount] = useState(null);
    const [defaultVerticalAccount, setDefaultVerticalAccount] = useState();
    const marketColumns = ['stock_price', 'market_value', 'per', 'pbr', 'psr', 'volume'];

    const country = dashboardState.companyInfo?.country;

    const [selectedCountry, setSelectedCountry] = useState(country ? country : 'JP');
    const [industryCompanies, setIndustryCompanies] = useState(null);

    useEffect(() => {
        if (selectableAccounts) {
            const paramHorizontal = searchParams.get("hAxis");
            const paramVertical = searchParams.get("vAxis");
    
            const horizontalAccount = getDescriptionBySettingName(userSettings, 'dashboard_industry_scatter_horizontal_axis');
            const verticalAccount = getDescriptionBySettingName(userSettings, 'dashboard_industry_scatter_vertical_axis');
    
            if (paramHorizontal) {
                const horizontalAccountDefinition = selectableAccounts.find((acc) => acc.col === paramHorizontal);
                if (horizontalAccountDefinition) {
                    setSelectedHorizontalAccount(horizontalAccountDefinition);
                }
            } else if (horizontalAccount) {
                const horizontalAccountDefinition = selectableAccounts.find((acc) => acc.col === horizontalAccount);
                if (horizontalAccountDefinition) {
                    setSelectedHorizontalAccount(horizontalAccountDefinition);
                }
            } else if (defaultHorizontalAccount) {  // default がある場合のみ設定
                setSelectedHorizontalAccount(defaultHorizontalAccount);
            }
    
            if (paramVertical) {
                const verticalAccountDefinition = selectableAccounts.find((acc) => acc.col === paramVertical);
                if (verticalAccountDefinition) {
                    setSelectedVerticalAccount(verticalAccountDefinition);
                }
            } else if (verticalAccount) {
                const verticalAccountDefinition = selectableAccounts.find((acc) => acc.col === verticalAccount);
                if (verticalAccountDefinition) {
                    setSelectedVerticalAccount(verticalAccountDefinition);
                }
            } else if (defaultVerticalAccount) {  // default がある場合のみ設定
                setSelectedVerticalAccount(defaultVerticalAccount);
            }
        }
    }, [selectableAccounts, searchParams, defaultHorizontalAccount, defaultVerticalAccount]); // defaultHorizontalAccount, defaultVerticalAccount は依存から除外
    
    useEffect(() => {
        if (selectableAccounts) {
            setDefaultHorizontalAccount(selectableAccounts.find((acc) => acc.col === 'sales'));
            setDefaultVerticalAccount(selectableAccounts.find((acc) => acc.col === 'operating_income'));
        }
    }, [selectableAccounts])

    useEffect(() => {
            const tabContent = tabContentRef.current;
            if (!tabContent) return; // refが設定されていない場合は何もしない
        
            // ResizeObserverを使って幅を監視
            const resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    const width = entry.contentRect.width;
                    // 幅が1000px以上であれば2列、それ以下であれば1列
                    setIsTwoColumns(width >= 1000);
                }
            });
        
            resizeObserver.observe(tabContent);
        
            // クリーンアップ処理
            return () => {
                resizeObserver.unobserve(tabContent);
            };
        }, [tabContentRef.current, loaded]);

    useEffect(() => {
        if (selectedHorizontalAccount && selectedVerticalAccount) {
            setGraphData(makeData(filteredIndividualLatestFinancialData, filteredIndustryMarketData));
        }
    }, [selectedHorizontalAccount, selectedVerticalAccount, filteredIndividualLatestFinancialData, selectedCountry])

    const getIndividualLatestData = (industryFinancialData) => {
        // 企業ごとの最新データを保持するオブジェクト
        const latestDataMap = {};
        if (industryFinancialData) {
            industryFinancialData.forEach((data) => {
            const secCode = data.sec_code;
    
            // 企業コードがまだない場合、または新しい年度のデータがある場合のみ更新
            if (!latestDataMap[secCode] || data.fiscal_year > latestDataMap[secCode].fiscal_year) {
                latestDataMap[secCode] = data;
            }
            });
        
            // オブジェクトの値を配列に変換して返す
            return Object.values(latestDataMap);
        }
        else {
            return null;
        }
    };
    
    useEffect(() => {
        const getIndustryFinancialData = async () => {
            if (dashboardState.industryFinancialData) {
                setIndustryFinancialData(dashboardState.industryFinancialData);
                const data = getIndividualLatestData(dashboardState.industryFinancialData);
                setIndividualLatestFinancialData(data);
            } else {
                const fetchedData = await fetchIndustryFinancialData(secCode, navigate, location);
                const industryData = fetchedData.data;
                setIndustryFinancialData(industryData);
                dashboardDispatch({ type: 'SET_INDUSTRY_FINANCIAL_DATA', payload: industryData})
                
                const data = getIndividualLatestData(industryData);
                setIndividualLatestFinancialData(data);
            }
        }

        const getIndustryInfo = async () => {
            if (dashboardState.industryInfo) {
                setIndustryInfo(dashboardState.industryInfo);
            } else {
                const fetchedData = await fetchIndustryInfo(secCode, navigate, location);
                const industryInfo = fetchedData.data;
                
                setIndustryInfo(industryInfo);
                dashboardDispatch({ type: 'SET_INDUSTRY_INFO', payload: industryInfo})
            }
        }

        const getIndustryMarketData = async () => {
            if (dashboardState.industryMarketData) {
                setIndustryMarketData(dashboardState.industryMarketData);
            } else {
                const fetchedData = await fetchIndustryMarketData(secCode, null, navigate, location);
                const marketData = fetchedData.data;
                setIndustryMarketData(marketData);
                dashboardDispatch({ type: 'SET_INDUSTRY_MARKET_DATA', payload: marketData})
            }
        }

        const getIndustryCompanies = async () => {
            if (dashboardState.industryCompanies) {
                setIndustryCompanies(dashboardState.industryCompanies);
            } else {
                const fetchedData = await fetchIndustryCompanies(secCode, navigate, location);
                const industryCompaniesData = fetchedData.data;
                setIndustryCompanies(industryCompaniesData);
                dashboardDispatch({ type: 'SET_INDUSTRY_COMPANIES', payload: industryCompaniesData})
            }
        }
        getIndustryFinancialData();
        getIndustryInfo();
        getIndustryMarketData();
        getIndustryCompanies();
    }, [secCode])

    useEffect(() => {
        const updateURLParams = () => {
          const newParams = new URLSearchParams(searchParams)
          if (selectedHorizontalAccount) {
            newParams.set("hAxis", selectedHorizontalAccount.col)
          }
          if (selectedVerticalAccount) {
            newParams.set("vAxis", selectedVerticalAccount.col)
          }
          setSearchParams(newParams)
        }
    
        updateURLParams()
      }, [selectedHorizontalAccount, selectedVerticalAccount, searchParams, setSearchParams])
    
    const onGraphSaveClick = async() => {
        const newHorizontalSetting = {
            setting_name: "dashboard_industry_scatter_horizontal_axis",
            description: selectedHorizontalAccount['col'],
        }
        await addUserSetting(newHorizontalSetting);
        const newVerticalSetting = {
            setting_name: "dashboard_industry_scatter_vertical_axis",
            description: selectedVerticalAccount['col'],
        }
        await addUserSetting(newVerticalSetting);
    }

    const makeData = (financialData, marketData) => {
        if (!financialData) {
            return null;
        }

        if ((marketColumns.includes(selectedHorizontalAccount) | marketColumns.includes(selectedVerticalAccount)) && !marketData) {
            return null;
        }

        if ((!marketColumns.includes(selectedHorizontalAccount) | !marketColumns.includes(selectedVerticalAccount)) && !financialData) {
            return null;
        }
        
        if (!industryCompanies) {
            return null;
        }
        const countrySecCodes = industryCompanies.filter((company) => company.country === selectedCountry).map((company => company.sec_code))
        const companies = financialData.filter((dt) => countrySecCodes.includes(dt.sec_code)
        ).map((dt) => {
            return {
                secCode: dt.sec_code,
                companyName: dt.company_name,
                reportedCurerncy: dt.reported_currency,
            }
        })
        let dataX;
        let dataY;
        if (marketColumns.includes(selectedHorizontalAccount.col)) {
            dataX = marketData;
        } else {
            dataX = financialData;
        }

        if (marketColumns.includes(selectedVerticalAccount.col)) {
            dataY = marketData;
        } else {
            dataY = financialData;
        }

        const data = companies.map((company) => {
            let xValue;
            let yValue;
            
            const x = dataX.find((dt) => dt.sec_code === company.secCode);
            if (x) {
                xValue = x[selectedHorizontalAccount.col === 'stock_price' ? 'close' : selectedHorizontalAccount.col];
            } else {
                xValue = null;
            }
            
            if (!dataY) {
                return null
            }
            const y = dataY.find((dt) => dt.sec_code === company.secCode);
            if (y) {
                yValue = y[selectedVerticalAccount.col];
            } else {
                yValue = null;
            }

            if (xValue && yValue) {
                return {
                    x: xValue,
                    y: yValue,
                    xTiming: marketColumns.includes(selectedHorizontalAccount.col) ? formatDateTimeString(x.date, 'YYYY年m月d日') : x.fiscal_year + '年度',
                    yTiming: marketColumns.includes(selectedVerticalAccount.col) ? formatDateTimeString(y.date, 'YYYY年m月d日') : y.fiscal_year + '年度',
                    companyName: company.companyName,
                    currency: company.reportedCurerncy,
                }
            } else {
                return null;
            }
        })

        const chartData = {
            companies: companies,
            data: data.filter((dt) => dt),
            name: [selectedHorizontalAccount.col_name, selectedVerticalAccount.col_name],
            unit: [selectedHorizontalAccount.unit, selectedVerticalAccount.unit]
        }
        return chartData;
    }

    useEffect(() => {
        if (filteredIndividualLatestFinancialData) {
            setGraphData(makeData(filteredIndividualLatestFinancialData, filteredIndustryMarketData));
        }
    }, [selectedHorizontalAccount, selectedVerticalAccount])
    
    useEffect(() => {
        if (!industryCompanies) {
            return 
        }

        const countrySecCodes = industryCompanies.filter((company) => company.country === selectedCountry).map((company => company.sec_code))
        
        if (industryFinancialData) {
            if (selectedCountry === 'JP') {
                const data = industryFinancialData.filter((dt) => {
                    if (countrySecCodes.includes(dt.sec_code)) {
                        return dt 
                    }
                })
                setFilteredIndustryFinancialData(data);
            } else if (selectedCountry === 'US') {
                const data = industryFinancialData.filter((dt) => {
                    if (countrySecCodes.includes(dt.sec_code)) {
                        return dt 
                    }
                })
                setFilteredIndustryFinancialData(data);
            }
        }
        
        if (individualLatestFinancialData) {
            if (selectedCountry === 'JP') {
                const data = individualLatestFinancialData.filter((dt) => {
                    if (countrySecCodes.includes(dt.sec_code)) {
                        return dt 
                    }
                })
                setfilteredIndividualLatestFinancialData(data);
            } else if (selectedCountry === 'US') {
                const data = individualLatestFinancialData.filter((dt) => {
                    if (countrySecCodes.includes(dt.sec_code)) {
                        return dt 
                    }
                })
                setfilteredIndividualLatestFinancialData(data);
            }
        }

        if (industryMarketData) {
            if (selectedCountry === 'JP') {
                const data = industryMarketData.filter((dt) => {
                    if (countrySecCodes.includes(dt.sec_code)) {
                        return dt 
                    }
                })
                setFilteredIndustryMarketData(data);
            } else if (selectedCountry === 'US') {
                const data = industryMarketData.filter((dt) => {
                    if (countrySecCodes.includes(dt.sec_code)) {
                        return dt 
                    }
                })
                setFilteredIndustryMarketData(data);
            }
        }
    }, [selectedCountry, individualLatestFinancialData, industryFinancialData, industryMarketData])

    if (!industryInfo) {
        return <Loading />
    }

    const options = [
        {id: 'JP', label: "日本"},
        {id: 'US', label: "米国"},
    ]

    const onCountryChange = (country) => {
        setSelectedCountry(country);
    }
    
    return (
        <div className="tabcontent" ref={tabContentRef}>
            <div className="mt-1 mb-2">
                <TwoColumnSwitch options={options} onChange={onCountryChange} defaultId={selectedCountry}/>
            </div>
            <CompanyInformation title='' description={industryInfo.industry_description} marginTop="0" />
            <div
                className={`grid gap-2 ${isTwoColumns ? "grid-cols-[5fr_3fr]" : "grid-cols-1"}`}
            >
                <div className="dashboard-row h-[600px] max-h-screen">
                <div className="flex flex-wrap min-h-[45px] items-center mb-5">
                    <div className="selected-accounts-container relative flex flex-wrap items-center">
                        <div className='selected-axis mr-2'>
                            <div className="axis-name">横軸:</div>
                            {selectedHorizontalAccount && <SelectScatterColumnContainer selectedAccount={selectedHorizontalAccount} setSelectedAccount={setSelectedHorizontalAccount} />}
                        </div>
                        <div className='selected-axis'>
                            <div className="axis-name">縦軸:</div>
                            {selectedVerticalAccount && <SelectScatterColumnContainer selectedAccount={selectedVerticalAccount} setSelectedAccount={setSelectedVerticalAccount} /> }
                        </div>
                    </div>
                    <SaveSettingButton onSaveClick={onGraphSaveClick} />
                </div>
                    {graphData && <ManualScatterGraph data={graphData} height={500} />}
                </div>
                {individualLatestFinancialData && <IndustryDataSummary industryFinancialData={filteredIndividualLatestFinancialData} industryMarketData={filteredIndustryMarketData} setLoaded={setLoaded} />}
            </div>
            {/* {industryFinancialData && <IndustryTimeSeries industryFinancialData={filteredIndustryFinancialData} />} */}
            {filteredIndividualLatestFinancialData && <IndustryRanking industryFinancialData={filteredIndividualLatestFinancialData} secCode={secCode} industryCompanies={industryCompanies} />}
            <SimilarCompanyContainer secCode={secCode} />
        </div>
    );
};

export default IndustryInformationTab;